import { bounds, ccw, type Pt, recenter, regular, SHAPE_PRESETS } from '../acoustics/polygon'
import type { MaterialId } from './materials'
import { type Enclosure, FAMILY_INFO, type Family, newId, type Primitive, type SceneDoc, type Source, type Vec3 } from './schema'

const allConcrete = (m: MaterialId = 'concrete'): Enclosure['materials'] => ({
  floor: m,
  ceiling: m,
  west: m,
  east: m,
  north: m,
  south: m,
  wall: m,
  core: m,
})

export function makeSource(family: Family, position: Vec3, name?: string, seed?: number): Source {
  return {
    id: newId('src'),
    name: name ?? FAMILY_INFO[family].label,
    position,
    kind: 'generator',
    family,
    params: [...FAMILY_INFO[family].defaults],
    seed: seed ?? Math.floor(Math.random() * 0xffffffff),
    clip: null,
    gain: 1,
    size: 1,
    mute: false,
    solo: false,
    playing: true,
    hfCut: family === 'vent' ? 7000 : 9000,
  }
}

export function makePrimitive(
  kind: Primitive['kind'],
  position: Vec3,
  size: Vec3,
  name?: string,
  material: MaterialId = 'concrete',
  rotation: Vec3 = [0, 0, 0],
  extra: { wall?: number; caps?: Primitive['caps']; outline?: Pt[]; holes?: Pt[][] } = {},
): Primitive {
  const label = kind === 'box' ? 'Block' : kind === 'tube' ? 'Tube' : kind === 'prism' ? 'Shape' : 'Pillar'
  return {
    id: newId('obj'),
    name: name ?? label,
    kind,
    position,
    rotation,
    size,
    material,
    wall: extra.wall ?? (kind === 'tube' ? Math.max(0.1, Math.min(size[0], size[2]) * 0.08) : 1),
    caps: extra.caps ?? 'none',
    outline: kind === 'prism' ? (extra.outline ?? SHAPE_PRESETS.hexagon.points()) : [],
    holes: kind === 'prism' ? (extra.holes ?? []) : [],
  }
}

/** A polygon room from a unit-square outline scaled to width × depth; centred, counter-clockwise, size = its bounds. */
export function polygonRoom(unit: Pt[], width: number, depth: number, height: number, materials: Enclosure['materials']): Enclosure {
  const { points } = recenter(ccw(unit.map(([x, z]) => [x * width, z * depth] as Pt)))
  const b = bounds(points)
  return { shape: 'polygon', core: 0, outline: points, size: [b.maxX - b.minX, height, b.maxZ - b.minZ], materials }
}

const baseSettings = { snapEnabled: true, snapStep: 0.5, masterGain: 0.8 }

export type PresetId =
  | 'starter'
  | 'turbine'
  | 'gantry'
  | 'abyss'
  | 'rotunda'
  | 'ringhall'
  | 'lhall'
  | 'crosshall'
  | 'hall'
  | 'chamber'
  | 'shaft'

export const PRESETS: { id: PresetId; label: string; description: string }[] = [
  { id: 'starter', label: 'Starter hall', description: 'Huge concrete hall with vent, motor behind a slab, and drips.' },
  {
    id: 'turbine',
    label: 'Turbine hall',
    description: '480 × 90 × 160 m. Six housings, crane rails, two machines you can hear from the far end.',
  },
  { id: 'gantry', label: 'Kilometre gantry', description: '1200 × 70 × 90 m. A column canyon with bulkheads; huge machines at 300+ m.' },
  { id: 'abyss', label: 'Abyss shaft', description: '160 × 600 × 160 m. A core column, catwalks, drips falling from far above.' },
  { id: 'rotunda', label: 'Rotunda', description: 'Round hall, 140 m across, with a ring of pillars and a resonant pipe in the middle.' },
  {
    id: 'ringhall',
    label: 'Ring hall',
    description: '500 m ring around a 160 m core column, with pipes running round it. Sound travels the long way round.',
  },
  {
    id: 'lhall',
    label: 'L-shaped works',
    description: 'Custom outline: two long arms meeting at a corner. Machines in one arm, you in the other.',
  },
  { id: 'crosshall', label: 'Cross hall', description: 'Custom outline: four arms around a central tank (an extruded shape with a hole).' },
  { id: 'hall', label: 'Vast hall (empty)', description: '100 × 30 × 60 m enclosure.' },
  { id: 'chamber', label: 'Long chamber (empty)', description: '60 × 6 × 8 m corridor-like chamber.' },
  { id: 'shaft', label: 'Shaft (empty)', description: '12 × 50 × 12 m vertical shaft.' },
]

export function createPreset(id: PresetId): SceneDoc {
  switch (id) {
    case 'hall':
      return {
        name: 'Vast hall',
        enclosure: { shape: 'box', core: 0, outline: [], size: [100, 30, 60], materials: allConcrete() },
        objects: [],
        sources: [],
        listener: { position: [0, 1.7, 22], yaw: 0, pitch: 0 },
        settings: { ...baseSettings },
      }
    case 'chamber':
      return {
        name: 'Long chamber',
        enclosure: { shape: 'box', core: 0, outline: [], size: [8, 6, 60], materials: allConcrete() },
        objects: [],
        sources: [],
        listener: { position: [0, 1.7, 24], yaw: 0, pitch: 0 },
        settings: { ...baseSettings },
      }
    case 'shaft':
      return {
        name: 'Shaft',
        enclosure: { shape: 'box', core: 0, outline: [], size: [12, 50, 12], materials: allConcrete() },
        objects: [],
        sources: [],
        listener: { position: [0, 1.7, 3], yaw: 0, pitch: 0 },
        settings: { ...baseSettings },
      }
    case 'turbine': {
      const objs: Primitive[] = []
      for (let i = 0; i < 6; i++) {
        const x = -180 + i * 72
        objs.push(makePrimitive('box', [x, 3, -30], [70, 6, 30], `Plinth ${i + 1}`))
        // Horizontal housing: a cylinder (axis = local Y) rotated about Z so it lies along X.
        objs.push(makePrimitive('cylinder', [x, 20, -30], [22, 60, 22], `Turbine ${i + 1}`, 'metal', [0, 0, Math.PI / 2]))
      }
      for (const z of [-62, 62]) objs.push(makePrimitive('box', [0, 72, z], [470, 3, 4], `Crane rail ${z < 0 ? 'N' : 'S'}`, 'metal'))
      for (let i = 0; i < 6; i++)
        for (const z of [-64, 64])
          objs.push(makePrimitive('box', [-200 + i * 80, 35, z], [6, 70, 6], `Column ${i + 1}${z < 0 ? 'N' : 'S'}`))
      const a = makeSource('turbine', [-146, 20, -30], 'Turbine 2', 3301)
      a.params = [0.3, 0.5, 0.3]
      a.size = 28
      a.gain = 0.3
      const b = makeSource('turbine', [70, 20, -30], 'Turbine 5', 3302)
      b.params = [0.22, 0.6, 0.45]
      b.size = 28
      b.gain = 0.3
      const vent = makeSource('fan', [210, 62, 60], 'Intake fan', 3303)
      vent.params = [0.25, 0.3, 0.4]
      vent.size = 18
      vent.gain = 0.35
      const drip = makeSource('drip', [-205, 0.3, 55], 'Condensate', 3304)
      drip.params = [0.18, 0.7, 0.8]
      drip.gain = 1
      return {
        name: 'Turbine hall',
        enclosure: { shape: 'box', core: 0, outline: [], size: [480, 90, 160], materials: { ...allConcrete(), ceiling: 'metal' } },
        objects: objs,
        sources: [a, b, vent, drip],
        listener: { position: [-60, 1.7, 52], yaw: -Math.PI / 2, pitch: 0 },
        settings: { ...baseSettings, snapStep: 2 },
      }
    }
    case 'gantry': {
      const objs: Primitive[] = []
      for (let i = 0; i < 12; i++)
        for (const z of [-36, 36])
          objs.push(makePrimitive('box', [-550 + i * 100, 35, z], [5, 70, 5], `Column ${i + 1}${z < 0 ? 'N' : 'S'}`))
      for (let i = 0; i < 5; i++) objs.push(makePrimitive('box', [-400 + i * 200, 20, 0], [3, 40, 58], `Bulkhead ${i + 1}`))
      objs.push(makePrimitive('box', [0, 58, 0], [1180, 2, 10], 'Overhead gantry', 'metal'))
      const motor = makeSource('press', [10, 8, -20], 'Mega press', 4401)
      motor.params = [0.1, 0.92, 0.35]
      motor.size = 45
      motor.gain = 0.25
      const drone = makeSource('creak', [420, 40, 15], 'Groaning structure', 4402)
      drone.params = [0.5, 0.12, 0.65]
      drone.size = 90
      drone.gain = 0.25
      const vent = makeSource('fan', [-300, 55, 30], 'Exhaust fan', 4403)
      vent.params = [0.3, 0.45, 0.35]
      vent.size = 25
      vent.gain = 0.3
      const drip = makeSource('drip', [-480, 0.3, 10], 'Leak', 4404)
      drip.params = [0.2, 0.8, 0.9]
      drip.gain = 1
      return {
        name: 'Kilometre gantry',
        enclosure: { shape: 'box', core: 0, outline: [], size: [1200, 70, 90], materials: allConcrete() },
        objects: objs,
        sources: [motor, drone, vent, drip],
        listener: { position: [-560, 1.7, 0], yaw: -Math.PI / 2, pitch: 0 },
        settings: { ...baseSettings, snapStep: 5 },
      }
    }
    case 'abyss': {
      const objs: Primitive[] = [makePrimitive('cylinder', [0, 280, 0], [44, 560, 44], 'Core column', 'metal')]
      for (const y of [120, 260, 400]) {
        for (const [i, rot] of [0, Math.PI / 2].entries())
          objs.push(makePrimitive('box', [0, y, 0], [140, 2, 8], `Catwalk ${y}-${i + 1}`, 'metal', [0, rot, 0]))
      }
      for (const y of [60, 190, 330, 470]) objs.push(makePrimitive('box', [58, y, 58], [24, 3, 24], `Ledge ${y}`))
      const drone = makeSource('drone', [40, 8, 12], 'Deep machinery', 5501)
      drone.params = [0.03, 0.4, 0.4]
      drone.size = 60
      drone.gain = 0.25
      const motor = makeSource('grind', [55, 250, 0], 'Lift cable', 5502)
      motor.params = [0.45, 0.3, 0.7]
      motor.size = 12
      motor.gain = 0.5
      const drip = makeSource('drip', [-40, 590, 30], 'Falling water', 5503)
      drip.params = [0.15, 0.9, 0.9]
      drip.gain = 1
      const vent = makeSource('vent', [60, 520, -60], 'Updraft', 5504)
      vent.size = 30
      vent.gain = 0.35
      return {
        name: 'Abyss shaft',
        enclosure: { shape: 'box', core: 0, outline: [], size: [160, 600, 160], materials: allConcrete() },
        objects: objs,
        sources: [drone, motor, drip, vent],
        listener: { position: [58, 262.7, 0], yaw: Math.PI / 2, pitch: -0.15 },
        settings: { ...baseSettings, snapStep: 2 },
      }
    }
    case 'rotunda': {
      const objs: Primitive[] = []
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2
        objs.push(makePrimitive('cylinder', [48 * Math.cos(a), 30, 48 * Math.sin(a)], [4, 60, 4], `Pillar ${i + 1}`))
      }
      objs.push(makePrimitive('tube', [0, 22, 0], [16, 44, 16], 'Resonant pipe', 'metal', [0, 0, 0], { wall: 0.8, caps: 'none' }))
      const pipe = makeSource('drone', [0, 18, 0], 'Pipe drone', 6601)
      pipe.params = [0.06, 0.5, 0.3]
      pipe.size = 10
      pipe.gain = 0.35
      const motor = makeSource('motor', [-40, 2, -30], 'Pump', 6602)
      motor.params = [0.14, 0.4, 0.3]
      motor.size = 6
      motor.gain = 0.4
      const vent = makeSource('vent', [55, 52, 20], 'Skylight draft', 6603)
      vent.size = 12
      vent.gain = 0.25
      const drip = makeSource('drip', [30, 0.4, -50], 'Drips', 6604)
      drip.params = [0.2, 0.7, 0.8]
      drip.gain = 1
      return {
        name: 'Rotunda',
        enclosure: { shape: 'round', core: 0, outline: [], size: [140, 60, 140], materials: allConcrete() },
        objects: objs,
        sources: [pipe, motor, vent, drip],
        listener: { position: [28, 1.7, 22], yaw: Math.PI * 0.75, pitch: 0 },
        settings: { ...baseSettings, snapStep: 1 },
      }
    }
    case 'ringhall': {
      const objs: Primitive[] = []
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2 + Math.PI / 8
        // A pipe lying along the ring's tangent: tilt the tube's axis onto the floor plane, then yaw it round.
        objs.push(
          makePrimitive('tube', [215 * Math.cos(a), 6, 215 * Math.sin(a)], [8, 110, 8], `Pipe ${k + 1}`, 'metal', [Math.PI / 2, -a, 0], {
            wall: 0.4,
            caps: 'none',
          }),
        )
      }
      const motor = makeSource('turbine', [200, 4, 0], 'Ring turbine', 7701)
      motor.params = [0.2, 0.6, 0.35]
      motor.size = 30
      motor.gain = 0.2
      const drone = makeSource('drone', [-170, 10, -30], 'Core hum', 7702)
      drone.params = [0.04, 0.4, 0.4]
      drone.size = 70
      drone.gain = 0.2
      const vent = makeSource('fan', [0, 70, -200], 'High fan', 7703)
      vent.params = [0.35, 0.4, 0.4]
      vent.size = 20
      vent.gain = 0.25
      const drip = makeSource('drip', [-20, 0.4, 150], 'Condensate', 7704)
      drip.params = [0.18, 0.7, 0.8]
      drip.gain = 1
      return {
        name: 'Ring hall',
        enclosure: { shape: 'ring', core: 160, outline: [], size: [500, 90, 500], materials: allConcrete() },
        objects: objs,
        sources: [motor, drone, vent, drip],
        listener: { position: [0, 1.7, 125], yaw: -Math.PI / 2, pitch: 0 },
        settings: { ...baseSettings, snapStep: 2 },
      }
    }
    case 'lhall': {
      // The long arm runs along the bottom of the unit L (z from -0.5 to 0); the short arm climbs from x = -0.5..0 up to z = 0.5.
      const objs: Primitive[] = []
      for (let i = 0; i < 4; i++)
        objs.push(
          makePrimitive('prism', [-90 + i * 40, 12, -42], [7, 24, 7], `Hex column ${i + 1}`, 'concrete', [0, 0, 0], {
            outline: regular(6),
          }),
        )
      objs.push(
        makePrimitive('prism', [60, 8, -40], [34, 16, 34], 'Boiler', 'metal', [0, 0, 0], {
          outline: regular(8, Math.PI / 8),
          holes: [regular(16).map(([x, z]) => [x * 0.7, z * 0.7] as Pt)],
        }),
      )
      const press = makeSource('press', [92, 6, -22], 'Forge press', 8801)
      press.params = [0.18, 0.85, 0.3]
      press.size = 14
      press.gain = 0.4
      const fan = makeSource('fan', [-80, 20, 30], 'Corner fan', 8802)
      fan.params = [0.3, 0.4, 0.35]
      fan.size = 10
      fan.gain = 0.4
      const grind = makeSource('grind', [-30, 3, -50], 'Scraper', 8803)
      grind.params = [0.4, 0.35, 0.65]
      grind.size = 4
      grind.gain = 0.5
      const creak = makeSource('creak', [-85, 15, 55], 'Roof creak', 8804)
      creak.params = [0.4, 0.2, 0.55]
      creak.size = 12
      creak.gain = 0.5
      return {
        name: 'L-shaped works',
        enclosure: polygonRoom(SHAPE_PRESETS.l.points(), 220, 140, 40, allConcrete()),
        objects: objs,
        sources: [press, fan, grind, creak],
        listener: { position: [-80, 1.7, 40], yaw: Math.PI, pitch: 0 },
        settings: { ...baseSettings, snapStep: 2 },
      }
    }
    case 'crosshall': {
      const objs: Primitive[] = [
        makePrimitive('prism', [0, 9, 0], [44, 18, 44], 'Central tank', 'metal', [0, 0, 0], {
          outline: regular(12, Math.PI / 12),
          holes: [regular(12, Math.PI / 12).map(([x, z]) => [x * 0.8, z * 0.8] as Pt)],
        }),
      ]
      for (const [i, [x, z]] of (
        [
          [0, -85],
          [85, 0],
          [0, 85],
          [-85, 0],
        ] as [number, number][]
      ).entries())
        objs.push(
          makePrimitive('prism', [x, 10, z], [14, 20, 14], `Pylon ${i + 1}`, 'concrete', [0, (i * Math.PI) / 2, 0], {
            outline: regular(3, -Math.PI / 2),
          }),
        )
      const turbine = makeSource('turbine', [0, 8, -105], 'North turbine', 9901)
      turbine.params = [0.3, 0.55, 0.3]
      turbine.size = 16
      turbine.gain = 0.35
      const creak = makeSource('creak', [105, 12, 0], 'East groan', 9902)
      creak.params = [0.45, 0.15, 0.6]
      creak.size = 20
      creak.gain = 0.4
      const press = makeSource('press', [-105, 6, 0], 'West press', 9903)
      press.params = [0.2, 0.9, 0.3]
      press.size = 14
      press.gain = 0.35
      const drip = makeSource('drip', [10, 0.4, 100], 'South drips', 9904)
      drip.params = [0.2, 0.7, 0.8]
      drip.gain = 1
      return {
        name: 'Cross hall',
        enclosure: polygonRoom(SHAPE_PRESETS.cross.points(), 260, 260, 45, allConcrete()),
        objects: objs,
        sources: [turbine, creak, press, drip],
        listener: { position: [0, 1.7, 110], yaw: 0, pitch: 0 },
        settings: { ...baseSettings, snapStep: 2 },
      }
    }
    default: {
      // 'starter'
      // A huge concrete hall; the listener stands near the south end, looking north down the hall.
      const slab = makePrimitive('box', [10, 4, 6], [0.6, 8, 16], 'Slab', 'concrete', [0, 0.35, 0])
      const pillars = [-24, -8, 8, 24].map((x, i) => makePrimitive('cylinder', [x, 7.5, -2], [2.4, 15, 2.4], `Pillar ${i + 1}`))
      const block = makePrimitive('box', [-18, 1.5, 14], [6, 3, 4], 'Block')
      const vent = makeSource('vent', [-30, 12, -26], 'Distant vent', 1017)
      const motor = makeSource('motor', [4, 1, 4], 'Motor behind slab', 2602)
      motor.params = [0.32, 0.4, 0.3]
      const drip = makeSource('drip', [-6, 0.4, -27], 'Far drips', 7741)
      drip.params = [0.22, 0.55, 0.8]
      drip.gain = 1.2
      return {
        name: 'Starter hall',
        enclosure: {
          shape: 'box',
          core: 0,
          outline: [],
          size: [100, 30, 64],
          materials: { ...allConcrete(), ceiling: 'concrete', floor: 'concrete' },
        },
        objects: [slab, ...pillars, block],
        sources: [vent, motor, drip],
        listener: { position: [26, 1.7, 14], yaw: Math.PI * 0.35, pitch: 0 },
        settings: { ...baseSettings },
      }
    }
  }
}
